import React from 'react';
import type { Area } from './types';

// ── Props ─────────────────────────────────────────────────────────────────────

export interface CanvasAreaProps {
  area: Area;
  // True once every box assigned to this Area is 100% complete (see
  // FlowchartBuilder.tsx's areaCompletionById, driven by lib/gantt.ts's
  // calcAreaCompletion) — turns the boundary green instead of the Area's own
  // color, the same "done" green (#10b981) CanvasBox.tsx's completion ring uses.
  isComplete: boolean;
  isSelected: boolean;
  isEditing: boolean;
  editingTitle: string;
  isDragging: boolean;

  onTitleChange: (v: string) => void;
  onTitleCommit: () => void;
  onStartDrag: (e: React.MouseEvent<SVGRectElement>) => void;
  onStartResize: (handle: 'nw' | 'ne' | 'sw' | 'se', e: React.MouseEvent<SVGRectElement>) => void;
  onDoubleClickTitle: () => void;
  onSelectTitle: () => void;
  onDelete: () => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

const CanvasArea = React.memo(function CanvasArea({
  area, isComplete, isSelected, isEditing, editingTitle, isDragging,
  onTitleChange, onTitleCommit,
  onStartDrag, onStartResize,
  onDoubleClickTitle, onSelectTitle, onDelete,
}: CanvasAreaProps) {
  const boundaryColor = isComplete ? '#10b981' : area.color;
  return (
    <g>
      {/* Wide invisible border for easier clicking */}
      <rect
        x={area.x} y={area.y} width={area.width} height={area.height}
        fill="none" stroke="transparent" strokeWidth={16}
        style={{ pointerEvents: 'auto', cursor: isDragging ? 'grabbing' : 'grab' }}
        onMouseDown={onStartDrag}
      />
      {/* Visible dashed border — green once every member box is 100% complete. */}
      <rect
        x={area.x} y={area.y} width={area.width} height={area.height}
        fill="none" stroke={boundaryColor} strokeWidth={isSelected ? 2.5 : 2} strokeDasharray="10,6"
        style={{ pointerEvents: 'none' }}
      />

      {/* Title in the top-left of the area */}
      {isEditing ? (
        <foreignObject x={area.x + 10} y={area.y + 8} width={Math.max(area.width - 20, 200)} height={38}>
          <input
            // @ts-ignore
            xmlns="http://www.w3.org/1999/xhtml"
            autoFocus
            value={editingTitle}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onTitleChange(e.target.value)}
            onBlur={onTitleCommit}
            onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === 'Escape') onTitleCommit(); }}
            style={{ background: 'rgba(255,255,255,0.9)', border: `1px solid ${area.color}`, borderRadius: 3, outline: 'none', fontSize: 22, fontWeight: 700, color: area.color, width: '100%', padding: '3px 6px', fontFamily: 'inherit' }}
          />
        </foreignObject>
      ) : (
        <text
          x={area.x + 12} y={area.y + 34}
          fontSize={22} fontWeight={700} fill={area.color}
          style={{ pointerEvents: 'auto', cursor: 'text', userSelect: 'none' }}
          onDoubleClick={onDoubleClickTitle}
          onMouseDown={(e) => { e.stopPropagation(); onSelectTitle(); }}
        >{area.title}</text>
      )}

      {/* Delete button outside the NE corner when selected */}
      {isSelected && (
        <g
          transform={`translate(${area.x + area.width + 14}, ${area.y - 14})`}
          style={{ pointerEvents: 'auto', cursor: 'pointer' }}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={onDelete}
        >
          <circle r={9} fill="white" stroke="#ef4444" strokeWidth={1.5} />
          <text textAnchor="middle" dominantBaseline="middle" fill="#ef4444" fontSize={14} fontWeight="bold" style={{ userSelect: 'none' }}>×</text>
        </g>
      )}

      {/* Corner handles for resizing when selected */}
      {isSelected && (['nw', 'ne', 'sw', 'se'] as const).map(handle => {
        const hx = (handle === 'ne' || handle === 'se') ? area.x + area.width : area.x;
        const hy = (handle === 'sw' || handle === 'se') ? area.y + area.height : area.y;
        const cur = (handle === 'nw' || handle === 'se') ? 'nwse-resize' : 'nesw-resize';
        return (
          <rect
            key={handle}
            x={hx - 6} y={hy - 6} width={12} height={12}
            fill="white" stroke={area.color} strokeWidth={1.5} rx={2}
            style={{ pointerEvents: 'auto', cursor: cur }}
            onMouseDown={(e) => onStartResize(handle, e)}
          />
        );
      })}
    </g>
  );
});

export default CanvasArea;
